import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField, NumberField } from '@/components/forms';
import { useCursorSelect } from '@/hooks/use-cursor-select';
import { appTzLabel, fromDate, isAfterAppToday, toDayjs } from '@/lib/date';

import { listOrderCustomers } from '../api';
import { toDefaults, toFilters } from '../filters';
import { ORDER_STATUS_CHIP } from '../format';
import { orderKeys, useOrderCities } from '../hooks';
import { orderFiltersSchema } from '../schemas';
import type { OrderCustomerOption, OrderFilterFormValues, OrderListFilters } from '../types';

import styles from './order-filter-drawer.module.scss';

const STATUS_OPTIONS = (Object.keys(ORDER_STATUS_CHIP) as (keyof typeof ORDER_STATUS_CHIP)[]).map(
  (value) => ({ value, label: ORDER_STATUS_CHIP[value].label }),
);

const customerToOption = (c: OrderCustomerOption) => ({ value: c.id, label: c.name });

export type OrderFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: OrderListFilters;
  onApply: (filters: OrderListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<OrderFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const draft = toDefaults(value);
  const form = useForm<OrderFilterFormValues>({
    resolver: zodResolver(orderFiltersSchema),
    defaultValues: {
      ...draft,
      orderedFrom: draft.orderedFrom ?? null,
      orderedTo: draft.orderedTo ?? null,
    },
    mode: 'onBlur',
  });

  const cities = useOrderCities();
  const cityOptions = useMemo(
    () => (cities.data ?? []).map((c) => ({ value: c, label: c })),
    [cities.data],
  );

  // Cursor-paginated, server-searched typeahead: scroll the popup to load more; a new search
  // term resets to the first page (see `useCursorSelect`). Cursor stays opaque to the FE.
  const customerSelect = useCursorSelect({
    queryKey: orderKeys.metaCustomers,
    fetchPage: async ({ search, cursor, limit }) => {
      const page = await listOrderCustomers({ search, cursor, limit });
      return {
        data: page.data,
        nextCursor: page.meta.nextCursor,
        hasNextPage: page.meta.hasNextPage,
      };
    },
    toOption: customerToOption,
  });

  // Single range control bound to the two draft fields — keeps the two-field wire contract and
  // the From<=To validation on orderedFrom/orderedTo while Figma 12:211846 draws one control.
  const [orderedFrom, orderedTo] = useWatch({
    control: form.control,
    name: ['orderedFrom', 'orderedTo'],
  });
  const orderedError = form.formState.errors.orderedTo?.message;

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="customerIds"
        control={form.control}
        label="Customers"
        placeholder="Select"
        showSearch={customerSelect.showSearch}
        loading={customerSelect.loading}
        onPopupScroll={customerSelect.onPopupScroll}
        options={customerSelect.options}
      />

      <MultiSelectField
        name="cities"
        control={form.control}
        label="City"
        placeholder="Select"
        options={cityOptions}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Order Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Order Date Range')}
          format="MM/DD/YYYY"
          disabledDate={isAfterAppToday}
          style={{ width: '100%' }}
          status={orderedError ? 'error' : undefined}
          // One-sided ranges stay supported: the schema, the wire contract (`orderedFrom` /
          // `orderedTo` are independent params) and AC §5's two-independently-clickable-pickers all
          // allow From-only or To-only. `allowEmpty` lets the control confirm a half-filled range,
          // and the value/onChange map each end separately — the shared `toDayjsRange` /
          // `fromDateRange` helpers collapse a partial range to `null`, which is what previously
          // made a one-sided filter impossible to set *or* redisplay (AC §12).
          allowEmpty={[true, true]}
          value={[toDayjs(orderedFrom) ?? null, toDayjs(orderedTo) ?? null]}
          onChange={(range) => {
            form.setValue('orderedFrom', fromDate(range?.[0]) ?? null, { shouldValidate: true });
            form.setValue('orderedTo', fromDate(range?.[1]) ?? null, { shouldValidate: true });
          }}
        />
        {orderedError ? <p className={styles.error}>{orderedError}</p> : null}
      </fieldset>

      <MultiSelectField
        name="statuses"
        control={form.control}
        label="Order Status"
        placeholder="Select"
        options={STATUS_OPTIONS}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Order Amount ($)</legend>
        <div className={styles.row}>
          <NumberField
            name="amountFrom"
            control={form.control}
            aria-label="Order Amount From"
            placeholder="From"
            min={0}
            max={999_999_999.99}
            precision={2}
            style={{ width: '100%' }}
          />
          <NumberField
            name="amountTo"
            control={form.control}
            aria-label="Order Amount To"
            placeholder="To"
            min={0}
            max={999_999_999.99}
            precision={2}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>No of Devices</legend>
        <div className={styles.row}>
          <NumberField
            name="deviceCountFrom"
            control={form.control}
            aria-label="No of Devices From"
            placeholder="From"
            min={1}
            max={100_000}
            precision={0}
            style={{ width: '100%' }}
          />
          <NumberField
            name="deviceCountTo"
            control={form.control}
            aria-label="No of Devices To"
            placeholder="To"
            min={1}
            max={100_000}
            precision={0}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>
    </Drawer>
  );
}

export function OrderFilterDrawer({ open, ...rest }: OrderFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
